import { useRef, useCallback, useEffect, useState } from 'react';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const apiCall = async (url, method = 'GET', body) => {
    const opts = { method, headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    const r = await fetch(url, opts);
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw e; }
    return r.json();
};

/**
 * useAutoSave — hook pro automatické ukládání bez tlačítka.
 *
 * @param {Function} onError - callback při chybě (obdrží error objekt)
 * @returns {{ save, saving }}
 *   - save(url, method, data, delay) — zavolá API s volitelným debounce (delay ms, 0 = ihned)
 *   - saving — boolean, true pokud probíhá uložení
 */
export function useAutoSave(onError) {
    const timers = useRef({});
    const [saving, setSaving] = useState(false);
    const saveCount = useRef(0);

    const showToastError = useCallback((error) => {
        window.dispatchEvent(new CustomEvent('in-app-toast', {
            detail: {
                id: crypto.randomUUID(),
                title: 'Chyba ukládání',
                body: error?.error || error?.message || 'Zkuste to znovu',
            },
        }));
        if (onError) onError(error);
    }, [onError]);

    const save = useCallback((url, method, data, delay = 0, key) => {
        const timerKey = key || `${method}:${url}`;

        // Clear existing timer for this key
        if (timers.current[timerKey]) {
            clearTimeout(timers.current[timerKey]);
            delete timers.current[timerKey];
        }

        const doSave = async () => {
            saveCount.current++;
            setSaving(true);
            try {
                const result = await apiCall(url, method, data);
                return result;
            } catch (e) {
                showToastError(e);
                return null;
            } finally {
                saveCount.current--;
                if (saveCount.current <= 0) {
                    saveCount.current = 0;
                    setSaving(false);
                }
            }
        };

        if (delay > 0) {
            return new Promise((resolve) => {
                timers.current[timerKey] = setTimeout(async () => {
                    delete timers.current[timerKey];
                    resolve(await doSave());
                }, delay);
            });
        }

        return doSave();
    }, [showToastError]);

    // Flush all pending timers
    const flush = useCallback(() => {
        Object.keys(timers.current).forEach(key => {
            clearTimeout(timers.current[key]);
            delete timers.current[key];
        });
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => flush();
    }, [flush]);

    return { save, saving, flush };
}

export { apiCall };
